<template>
  <div class="settings-block">
    <div class="settings-block__title">
      <div class="settings-block__title-text">
        {{ t('Profile') }}
      </div>
      <div class="settings-block__title-buttons">
        <ui-button
          v-show="!edit"
          :text="t('Edit')"
          icon="fas fa-edit"
          button-style="outline-accent"
          @click="edit = true"
        />

        <ui-button
          v-show="edit"
          :text="t('Cancel')"
          icon="fas fa-times"
          :disabled="apiLoading"
          @click="cancel()"
        />

        <ui-button
          v-show="edit"
          :text="t('Save')"
          icon="fas fa-check"
          button-style="accent"
          :loading="apiLoading"
          @click="submit"
        />
      </div>
    </div>

    <div class="settings-block__content" v-if="!store.user.isMember">
      <div class="mb-16 profile-container">
        <div class="email-container">
          <ui-icon
            size="16"
            :color="$colors.green"
            class="mr-4 icon"
            icon="fas fa-envelope"
          />
          <div class="email">{{ form.email }}</div>
        </div>
        <span class="currency ml-16">
          <span class="currencySymbol">{{ currencySymbol }}</span>
          {{ form.currencyId }}
        </span>
      </div>

      <div class="row-2-columns-wrp">
        <ui-select-language
          class="row-2-columns-left"
          :showLabel="true"
          @update:model-value="changeLanguage"
          :modelValue="form.language"
          :disabled="!edit || apiLoading"
        />
      </div>
      <div class="row-2-columns-wrp">
        <ui-input
          class="row-2-columns-left"
          v-model="form.firstName"
          :label="t('First Name')"
          :placeholder="t('First Name')"
          name="first_name"
          :disabled="!edit || apiLoading"
          :error-messages="fieldErrors.firstName"
        />
        <ui-input
          class="row-2-columns-right"
          v-model="form.lastName"
          :label="t('Last Name')"
          :placeholder="t('Last Name')"
          name="last_name"
          :disabled="!edit || apiLoading"
          :error-messages="fieldErrors.lastName"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch, onMounted } from 'vue';
import { pick } from 'lodash';
import { useColors, useI18n } from '@/composables';
import { useStore } from '@/store';
import { patchUserApiRequest } from '@/services/api/user-api';
import type { FieldErrors, User } from '@/services/models';
import parseApiErrors from '@/utils/parseApiErrors';
import { loadLanguageAsync } from '@/i18n';
import { $emitter } from '@/services/emitter';
import currencyList from '@/components/helpers/currency-list';

const { t } = useI18n();
const $colors = useColors();
const store = useStore();

const apiLoading = ref(false);
const fieldErrors = ref<FieldErrors>({});
const edit = ref(false);

type ProfileForm = Pick<
  User,
  'lastName' | 'firstName' | 'email' | 'currencyId' | 'language'
>;

const form = reactive<ProfileForm>({
  lastName: '',
  firstName: '',
  email: '',
  currencyId: '',
  language: '',
});

const currencySymbol = computed(
  () => currencyList[form.currencyId as keyof typeof currencyList],
);

const fillForm = (source: User) => {
  Object.assign(
    form,
    pick(source, ['lastName', 'firstName', 'email', 'currencyId', 'language']),
  );
};

const cancel = () => {
  fillForm(store.user as User);
  edit.value = false;
  fieldErrors.value = {};
};

const changeLanguage = (data: string) => {
  form.language = data;
};

const apiSubmit = async (
  payload: Pick<ProfileForm, 'language' | 'firstName' | 'lastName'>,
): Promise<boolean> => {
  fieldErrors.value = {};
  apiLoading.value = true;

  try {
    const res = await patchUserApiRequest(payload);
    const userPayload = res.data.payload;

    store.$patch(state => {
      state.user = { ...state.user, ...userPayload };
    });

    $emitter.emit('toast-success', t('Changes have been successfully saved'));
    await loadLanguageAsync(String(payload.language));
    return true;
  } catch (error: unknown) {
    fieldErrors.value = parseApiErrors({ error });
    return false;
  } finally {
    apiLoading.value = false;
  }
};

const submit = async () => {
  const success = await apiSubmit({
    language: form.language,
    firstName: form.firstName,
    lastName: form.lastName,
  });
  if (success) {
    edit.value = false;
  }
};

watch(
  () => store.user,
  () => {
    fillForm(store.user as User);
  },
  { deep: true },
);

onMounted(() => fillForm(store.user as User));
</script>

<style lang="scss" scoped>

.profile-container {
  display: flex;
  justify-content: space-between;
}

.email-container {
  display: flex;
  align-items: start;

  .icon {
    margin-top: 5px;
  }
}
.email {
  font-size: 16px;
  font-weight: bold;
  word-break: break-all;
}

.currency {
  display: flex;
  font-size: 16px;
  font-weight: bold;
}

.currencySymbol {
  color: var(--color-green);
  margin-right: 2px;
}
</style>
